/**
 * Audit PP1 — Paper-Trading-Konto-Aggregator.
 *
 * Single Source of Truth für Equity / Cash / Invested / Realized + Unrealized
 * P&L / Win-Rate / Drawdown / Konzentration / offenes Risiko.
 *
 * Wird in AccountOverviewPanel + Dashboard verwendet.
 */

import type { Position } from '@/types';
import type { UserSettings } from '@/lib/queries';

export interface AccountSummary {
  /** Settings.account_balance — entspricht aktueller Konto-Größe (in Account-Currency). */
  account_balance_setting: number;
  account_currency: string;

  /** account_balance + realisiert(closed) + unrealisiert(open). */
  equity: number;
  /** equity − investiertes Kapital. */
  free_cash: number;
  /** sum(quantity * current_price * fx) für offene Positionen. */
  invested_capital: number;
  invested_pct: number;

  realized_pnl: number;
  unrealized_pnl: number;
  total_pnl: number;
  total_pnl_pct: number;

  /** Risk-Side aus offenen Positionen: sum(quantity*(entry−current_stop)*fx). */
  total_open_risk: number;
  total_open_risk_pct: number;

  /** Trade-Statistiken aus geschlossenen Positionen. */
  open_count: number;
  closed_count: number;
  win_count: number;
  loss_count: number;
  win_rate: number | null;
  avg_r: number | null;
  avg_winner_native: number | null;
  avg_loser_native: number | null;
  profit_factor: number | null;
  avg_holding_days: number | null;
  biggest_position_value: number;
  biggest_winner_pnl: number | null;
  biggest_loser_pnl: number | null;

  /** Konzentrationsmetrik: größter Positionswert / invested. 0…1. */
  concentration_top1: number | null;

  /** Equity-Drawdown gerechnet aus geschlossenen + offenen Trades. */
  current_drawdown_pct: number | null;
  max_drawdown_pct: number | null;

  /** TT1: heute realisiert (Trades, die heute geschlossen wurden) — in Account-Currency. */
  realized_today: number;
  /** TT1: heute angefallenes unrealisiertes Delta — Veränderung der unrealized P&L gegenüber gestern, falls trackbar. Aktuell = 0 (kein historischer Snapshot vorhanden). */
  unrealized_delta_today: number;
  /** TT1: Equity-Verlauf für Mini-Sparkline. Punkte chronologisch, je geschlossenem Trade ein Punkt + aktueller Stand. */
  equity_curve: Array<{ at: string; equity: number }>;

  /** Bewertungs-Texte für UI. */
  assessment: AccountAssessment;
}

export interface AccountAssessment {
  tone: 'good' | 'neutral' | 'warn';
  headline: string;
  notes: string[];
}

function realizedFromPosition(p: Position): number {
  // realized_pnl_account ist nur in closed-Positionen gefüllt.
  return p.realized_pnl_account ?? 0;
}

function unrealizedFromPosition(p: Position): number {
  return p.unrealized_pnl_account ?? p.current_pnl_abs_account ?? 0;
}

function positionValueAccount(p: Position): number {
  // Live-Positionswert in Account-Currency (Entry-FX als Approximation)
  if (p.current_price === null || !Number.isFinite(p.current_price)) return 0;
  return p.current_price * p.quantity * (p.fx_rate_at_entry || 1);
}

function positionRiskAccount(p: Position): number {
  // Long: Risiko = quantity*(entry − current_stop)*fx, nur positiv
  const perShare = Math.max(0, p.avg_entry_price - p.current_stop);
  return perShare * p.quantity * (p.fx_rate_at_entry || 1);
}

function holdingDays(p: Position): number | null {
  if (!p.entry_at) return null;
  const start = Date.parse(p.entry_at);
  const end = p.exit_at ? Date.parse(p.exit_at) : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
}

/**
 * Equity-Drawdown grob: wir simulieren eine Equity-Kurve aus chronologisch
 * sortierten geschlossenen Trades + aktuelles Unrealized — und tracken Peak.
 */
function computeDrawdownPct(
  startBalance: number,
  closed: Position[],
  unrealizedNow: number
): { current: number | null; max: number | null } {
  if (startBalance <= 0) return { current: null, max: null };
  const sorted = [...closed]
    .filter((p) => p.exit_at)
    .sort((a, b) => Date.parse(a.exit_at!) - Date.parse(b.exit_at!));
  let equity = startBalance;
  let peak = startBalance;
  let maxDD = 0;
  for (const p of sorted) {
    equity += realizedFromPosition(p);
    if (equity > peak) peak = equity;
    const dd = peak > 0 ? ((peak - equity) / peak) * 100 : 0;
    if (dd > maxDD) maxDD = dd;
  }
  // Aktuell mit unrealized
  const equityNow = equity + unrealizedNow;
  const peakNow = Math.max(peak, equityNow);
  const currentDD = peakNow > 0 ? ((peakNow - equityNow) / peakNow) * 100 : 0;
  if (currentDD > maxDD) maxDD = currentDD;
  return { current: currentDD, max: maxDD };
}

function pickAssessment(s: Omit<AccountSummary, 'assessment'>): AccountAssessment {
  const notes: string[] = [];
  let tone: 'good' | 'neutral' | 'warn' = 'neutral';

  if (s.invested_pct > 90) {
    notes.push('Kapital fast voll gebunden — wenig Reserven für neue Setups.');
    tone = 'warn';
  } else if (s.invested_pct > 70) {
    notes.push('Kapital stark gebunden.');
  } else if (s.invested_pct < 30 && s.open_count > 0) {
    notes.push('Hoher Cash-Anteil — Spielraum für weitere Setups.');
  }

  if (s.total_open_risk_pct > 6) {
    notes.push(`Offenes Gesamtrisiko ${s.total_open_risk_pct.toFixed(1)} % — über konservativem 5-%-Korridor.`);
    tone = 'warn';
  } else if (s.total_open_risk_pct > 3) {
    notes.push(`Offenes Gesamtrisiko ${s.total_open_risk_pct.toFixed(1)} % — moderat.`);
  }

  if ((s.concentration_top1 ?? 0) > 0.4) {
    notes.push(`Größte Position bindet ${((s.concentration_top1 ?? 0) * 100).toFixed(0)} % des investierten Kapitals — Konzentrationsrisiko.`);
    if (tone !== 'warn') tone = 'warn';
  }

  if (s.current_drawdown_pct !== null && s.current_drawdown_pct > 8) {
    notes.push(`Aktueller Drawdown ${s.current_drawdown_pct.toFixed(1)} % — neue Trades vorsichtig dimensionieren.`);
    tone = 'warn';
  }

  if (s.profit_factor !== null && s.closed_count >= 10) {
    if (s.profit_factor > 1.5) {
      notes.push(`Profit-Factor ${s.profit_factor.toFixed(2)} aus ${s.closed_count} Trades — solide Performance.`);
      if (tone === 'neutral') tone = 'good';
    } else if (s.profit_factor < 1) {
      notes.push(`Profit-Factor ${s.profit_factor.toFixed(2)} — Strategie liefert aktuell keinen Edge.`);
      tone = 'warn';
    }
  }

  let headline: string;
  if (tone === 'warn') headline = 'Risiko aktuell erhöht';
  else if (tone === 'good') headline = 'Konto stabil mit positivem Edge';
  else if (s.open_count === 0) headline = 'Konto neutral — keine offenen Trades';
  else headline = 'Konto stabil';

  return { tone, headline, notes };
}

export function computeAccountSummary(
  settings: Pick<UserSettings, 'account_balance' | 'account_currency'>,
  openPositions: Position[],
  closedPositions: Position[]
): AccountSummary {
  const startBal = settings.account_balance;
  const realized = closedPositions.reduce((sum, p) => sum + realizedFromPosition(p), 0);
  const unrealized = openPositions.reduce((sum, p) => sum + unrealizedFromPosition(p), 0);
  const equity = startBal + realized + unrealized;

  const investedCapital = openPositions.reduce(
    (sum, p) => sum + positionValueAccount(p),
    0
  );
  const investedPct = equity > 0 ? (investedCapital / equity) * 100 : 0;
  const freeCash = Math.max(0, equity - investedCapital);

  const totalOpenRisk = openPositions.reduce((sum, p) => sum + positionRiskAccount(p), 0);
  const totalOpenRiskPct = equity > 0 ? (totalOpenRisk / equity) * 100 : 0;

  const realizedList = closedPositions.map(realizedFromPosition);
  const winners = realizedList.filter((v) => v > 0);
  const losers = realizedList.filter((v) => v < 0);
  const winRate = closedPositions.length > 0 ? winners.length / closedPositions.length : null;
  const sumWinners = winners.reduce((s, v) => s + v, 0);
  const sumLosersAbs = losers.reduce((s, v) => s + Math.abs(v), 0);
  const profitFactor = sumLosersAbs > 0 ? sumWinners / sumLosersAbs : null;
  const avgWinnerNative = winners.length > 0 ? sumWinners / winners.length : null;
  const avgLoserNative = losers.length > 0 ? -sumLosersAbs / losers.length : null;
  const rValues = closedPositions
    .map((p) => p.final_r_multiple)
    .filter((v): v is number => v !== null && Number.isFinite(v));
  const avgR = rValues.length > 0 ? rValues.reduce((s, v) => s + v, 0) / rValues.length : null;

  const holdingDaysList = closedPositions
    .map(holdingDays)
    .filter((v): v is number => v !== null);
  const avgHoldDays =
    holdingDaysList.length > 0
      ? holdingDaysList.reduce((s, v) => s + v, 0) / holdingDaysList.length
      : null;

  const biggestPosVal = openPositions.reduce(
    (max, p) => Math.max(max, positionValueAccount(p)),
    0
  );
  const concentration =
    investedCapital > 0 ? biggestPosVal / investedCapital : null;
  const biggestWinnerPnl = realizedList.length > 0 ? Math.max(...realizedList, 0) : null;
  const biggestLoserPnl = realizedList.length > 0 ? Math.min(...realizedList, 0) : null;

  const dd = computeDrawdownPct(startBal, closedPositions, unrealized);

  // TT1: Heutige Realisierungen
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const realizedToday = closedPositions
    .filter((p) => p.exit_at && Date.parse(p.exit_at) >= todayStart.getTime())
    .reduce((sum, p) => sum + realizedFromPosition(p), 0);

  // TT1: Equity-Curve aus chronologischen Closed-Trades + aktueller Stand
  const sortedClosed = [...closedPositions]
    .filter((p) => p.exit_at)
    .sort((a, b) => Date.parse(a.exit_at!) - Date.parse(b.exit_at!));
  const curve: Array<{ at: string; equity: number }> = [];
  let runningEquity = startBal;
  curve.push({ at: '', equity: runningEquity });
  for (const p of sortedClosed) {
    runningEquity += realizedFromPosition(p);
    curve.push({ at: p.exit_at!, equity: runningEquity });
  }
  // letzter Punkt: aktuelle Equity inkl. unrealized
  curve.push({ at: new Date().toISOString(), equity: runningEquity + unrealized });

  const summaryWithoutAssessment: Omit<AccountSummary, 'assessment'> = {
    account_balance_setting: startBal,
    account_currency: settings.account_currency,
    equity,
    free_cash: freeCash,
    invested_capital: investedCapital,
    invested_pct: investedPct,
    realized_pnl: realized,
    unrealized_pnl: unrealized,
    total_pnl: realized + unrealized,
    total_pnl_pct: startBal > 0 ? ((realized + unrealized) / startBal) * 100 : 0,
    total_open_risk: totalOpenRisk,
    total_open_risk_pct: totalOpenRiskPct,
    open_count: openPositions.length,
    closed_count: closedPositions.length,
    win_count: winners.length,
    loss_count: losers.length,
    win_rate: winRate,
    avg_r: avgR,
    avg_winner_native: avgWinnerNative,
    avg_loser_native: avgLoserNative,
    profit_factor: profitFactor,
    avg_holding_days: avgHoldDays,
    biggest_position_value: biggestPosVal,
    biggest_winner_pnl: biggestWinnerPnl,
    biggest_loser_pnl: biggestLoserPnl,
    concentration_top1: concentration,
    current_drawdown_pct: dd.current,
    max_drawdown_pct: dd.max,
    realized_today: realizedToday,
    unrealized_delta_today: 0, // braucht Snapshot — heute nicht trackbar
    equity_curve: curve,
  };

  return {
    ...summaryWithoutAssessment,
    assessment: pickAssessment(summaryWithoutAssessment),
  };
}
